import { Component, inject, Input } from '@angular/core';
import { AsyncPipe, NgClass, NgForOf, NgIf, TitleCasePipe, UpperCasePipe } from '@angular/common';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs';
import { Link } from '@shared/interfaces/link';
import { AuthService } from '@shared/services/auth.service';
import { LetDirective } from '@shared/directives/let.directive';
import { UserService } from '@shared/services/user.service';
import { NavBannerComponent } from '@cargeny/shared/components/nav/components/nav-banner/nav-banner.component';
import { ClickOutsideDirective } from '@shared/directives/click-outside.directive';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ENVIRONMENT_CONFIG } from '@shared/injection-token/environment-config';
import { setLang } from '@shared/utils/set-lang';

@Component({
  selector: 'app-nav',
  imports: [
    LetDirective,
    NgIf,
    NgForOf,
    RouterLink,
    AsyncPipe,
    TitleCasePipe,
    NavBannerComponent,
    NgClass,
    ClickOutsideDirective,
    UpperCasePipe,
    TranslateModule,
  ],
  templateUrl: './nav.component.html',
})
export class NavComponent {
  @Input() isBanner = false;
  @Input() isDashboard = false;
  links: Link[];
  openedLinkIndex?: number;
  isOpen = false;
  isOpenLang = false;
  currentLang: string;
  availableLang: string[];

  user$ = this.userService.getUser$();

  environmentConfig = inject(ENVIRONMENT_CONFIG);
  routesPath = this.environmentConfig.routes;
  serviceRoutesPath = this.environmentConfig.serviceRoutes;

  constructor(
    private router: Router,
    private authService: AuthService,
    private userService: UserService,
    private translateService: TranslateService
  ) {
    this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe(() => {
      this.isOpen = false;
      this.openedLinkIndex = undefined;
    });
    this.currentLang = this.translateService.currentLang;
    this.availableLang = this.environmentConfig.langs;
    this.translateService.onLangChange.subscribe((newLang) => (this.currentLang = newLang.lang));
    this.links = [
      {
        url: '',
        label: 'NAV.MENU.HOME',
      },
      {
        url: this.routesPath.articles,
        label: 'NAV.MENU.ARTICLES',
      },
      {
        label: 'NAV.MENU.SERVICES',
        dropdown: [
          {
            url: `${this.routesPath.services}/${this.serviceRoutesPath['purchase-of-a-vehicle']}`,
            label: `SERVICES.PURCHASE_OF_A_VEHICLE.TITLE`,
          },
          {
            url: `${this.routesPath.services}/${this.serviceRoutesPath['sale-of-a-vehicle']}`,
            label: `SERVICES.SALE_OF_A_VEHICLE.TITLE`,
          },
          {
            url: `${this.routesPath.services}/${this.environmentConfig.serviceRoutes['real-time-valuation']}`,
            label: `SERVICES.REAL_TIME_VALUATION.TITLE`,
          },
        ],
      },
      {
        label: 'NAV.MENU.ASSISTANCE',
        dropdown: [
          {
            url: this.environmentConfig.globalRoutes['frequently-asked-questions'],
            label: `NAV.MENU.FAQ`,
          },
          {
            url: this.routesPath.contact,
            label: `NAV.MENU.CONTACT`,
          },
        ],
      },
    ];
  }

  onLogout() {
    this.authService.logout();
  }

  onClickOutsideDropdown() {
    this.openedLinkIndex = undefined;
  }

  onClickDropdown(i: number, event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.openedLinkIndex === i ? (this.openedLinkIndex = undefined) : (this.openedLinkIndex = i);
  }

  onChangeLang(selectedLang: string) {
    this.isOpenLang = false;
    setLang(selectedLang, this.environmentConfig);
    this.translateService.use(selectedLang);
  }

  onClickLang(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isOpenLang = !this.isOpenLang;
  }
}
